import React, { useState, useEffect } from 'react';
import api from '../api';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import MessageBox from '../components/general/MessageBox';
import { useNavigate } from 'react-router-dom';
import ListGroup from 'react-bootstrap/ListGroup';
import { useContext } from 'react';
import { Store } from '../Store';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { getFromIndexedDB } from '../indexedDB';
import { formatEuro, getError } from '../utils';
import LoadingLogo from '../components/general/LoadingLogo';
import { cartQuantityFromInput } from '../utils/formInputs';
import { itemsGross } from '../utils/orderTotals';

export default function CartScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate(); // enables the functionality, to navigate a user to a different screen
  const { state, dispatch: ctxDispatch } = useContext(Store); // useContext gives me access to the state of the context (in this case the global Store) and lets me rename dispatch to ctxDisptach, to distinguish it from the dispatch of the current component
  const {
    cart: { cartItems },
    userInfo,
  } = state; // using destructuring assignment, to filter the cartItems state out of the Store

  const bucketName =
    process.env.NODE_ENV === 'production'
      ? 'custom-keycaps-prod-designs'
      : 'custom-keycaps-designs'; // conditionally seting the S3 bucket for design thumbnails, based on the NODE_ENV

  const [assets, setAssets] = useState({}); // Local state to store fetched data from IndexedDB
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const preloadImage = (url) =>
    new Promise((resolve) => {
      if (!url) {
        // If there's no URL, resolve immediately to avoid stalling everything
        return resolve();
      }
      const img = new Image();
      img.src = url;
      img.onload = () => resolve();
      img.onerror = () => resolve(); // or you can reject(), but usually you still want to proceed
    }); // Helper: load a single URL and return a promise

  useEffect(() => {
    const fetchAssets = async () => {
      setLoading(true);
      setError(''); // a failure of an earlier run must not outlive this one

      try {
        const assetsMap = {};

        // 1) Build an array of *all* URLs to load
        const urlsToLoad = [];

        // 2) For items with uniqueCartId => fetch from IndexedDB
        for (const item of cartItems) {
          if (item.uniqueCartId) {
            const data = await getFromIndexedDB(item.uniqueCartId);
            if (data) {
              // Store { pngDataUrl } for later usage
              assetsMap[item.uniqueCartId] = {
                pngDataUrl: data.pngDataUrl,
              };

              urlsToLoad.push(data.pngDataUrl); // Add the local PNG URL to the array of images to preload
            }
          }
        }

        // 3) For items with designId => fetch signedThumbnailUrl
        const designIds = cartItems
          .filter((item) => item.designId)
          .map((item) => item.designId);

        if (designIds.length > 0) {
          try {
            if (userInfo) {
              // a) Fetch the signed thumbnail URLs
              const { data: signedUrls } = await api.post(
                '/api/designs/thumbnails',
                { designIds }
              );

              // b) Populate assetsMap with the returned signed URLs
              for (const designId of designIds) {
                const url = signedUrls.thumbnails?.[designId];
                if (url) {
                  // Initialize if not present:
                  if (!assetsMap[designId]) {
                    assetsMap[designId] = {};
                  }
                  assetsMap[designId].signedThumbnailUrl = url;

                  urlsToLoad.push(url); // Add to preload array
                }
              }

              // c) Now check ownership
              const { data: ownershipRes } = await api.post(
                '/api/designs/check-owner',
                { designIds }
              );

              for (const designId of designIds) {
                // If assetsMap[designId] doesn't exist, create it
                if (!assetsMap[designId]) {
                  assetsMap[designId] = {};
                }
                assetsMap[designId].isOwned =
                  ownershipRes.ownership?.[designId] || false;
              }
            } else {
              // User is not logged in => by default, designs with designId are not owned
              for (const designId of designIds) {
                if (!assetsMap[designId]) {
                  assetsMap[designId] = {};
                }
                const fallbackUrl = `https://${bucketName}.s3.eu-central-1.amazonaws.com/design-${designId}.png`;
                assetsMap[designId].signedThumbnailUrl = fallbackUrl;
                assetsMap[designId].isOwned = false;
                urlsToLoad.push(fallbackUrl);
              }
            }
          } catch (err) {
            // no error state here: the fallback URLs below are the handling, and an error set here replaces the whole cart with its message (until Oct 2026 it was the Error object itself, which blanked the page)
            console.error(
              'Failed to fetch design thumbnails or ownership:',
              err
            );
            // If there's an error, fall back to something safe
            for (const designId of designIds) {
              if (!assetsMap[designId]) {
                assetsMap[designId] = {};
              }
              const fallbackUrl = `https://${bucketName}.s3.eu-central-1.amazonaws.com/design-${designId}.png`;
              assetsMap[designId].signedThumbnailUrl = fallbackUrl;
              assetsMap[designId].isOwned = false;
              urlsToLoad.push(fallbackUrl);
            }
          }
        }

        // 4) Preload all images
        await Promise.all(urlsToLoad.map((url) => preloadImage(url)));

        // 5) Finally, store assetsMap, turn off loading
        setLoading(false);
        setAssets(assetsMap);
      } catch (error) {
        setLoading(false);
        setError(getError(error));
        console.error('Failed to fetch thumbnails:', error);
      }
    };

    fetchAssets();
  }, [cartItems, userInfo]); // Fetch assets (pngDataUrl or signed thumbnail URL) for each cart item

  const handleWheel = (e) => {
    e.target.blur(); // Unfocus the input to prevent scrolling from changing the value
    e.preventDefault(); // Prevent the default scroll behavior
    setTimeout(() => e.target.focus(), 0); // Refocus it after the wheel event
  };

  const handleBlur = (e, item) => {
    if (e.target.value === '' || e.target.value === '0') {
      e.target.value = '1';
    } // updates the field value to 1, if it is empty or 0

    updateCartHandler(item, e.target.value);
  };

  const updateCartHandler = async (item, quantity) => {
    ctxDispatch({
      type: 'CART_UPDATE_ITEM',
      payload: { ...item, quantity: cartQuantityFromInput(quantity) }, // 1 to 100, 1 for anything but a number
    }); // updates the local storage in the Store
  };

  const removeItemHandler = (item) => {
    // a dispatch to the Store
    ctxDispatch({ type: 'CART_REMOVE_ITEM', payload: item }); // updates the local storage in the Store
  };

  const checkoutHandler = () => {
    if (userInfo) {
      navigate('/shipping-address');
    } else {
      navigate('/signin');
    }
  };

  return (
    <Container>
      <div>
        <title>{t('cart-screen-helmet')}</title>

        <h1 className="screen-heading">{t('cart-screen-heading')}</h1>

        {loading ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <Row className="d-flex justify-content-center">
            {/* for this Row, there are being following Col(umns) being showed on the page: */}
            <Col md={12} lg={10} className="cart-card">
              <Row>
                {cartItems.length === 0 ? (
                  <div className="w-100">
                    <MessageBox className="default-alert">
                      {/* if  there are no items in the cart, the MessageBox component (that I created) will be shown */}
                      {t('cart-screen-empty-cart')}
                    </MessageBox>
                  </div>
                ) : (
                  <ListGroup style={{ padding: '0px' }}>
                    {/* ListGroup lets you display a list of items (component from react-bootstrap) */}
                    {cartItems.map((item, index) => (
                      /* for every item in the cart, display the following: */
                      <div key={item.uniqueCartId || item.designId}>
                        {index !== 0 && <div className="divider"></div>}

                        <ListGroup.Item
                          className="cart-item"
                          key={item.uniqueCartId || item.designId}
                        >
                          <Row className="cart-row">
                            <Col
                              xs={2}
                              sm={3}
                              className="d-flex justify-content-start align-items-center"
                            >
                              {/* If the item has an uniqueCartId, the design is saved in the indexedDB of the users browser and the user should be displayed with the 'Open in Customizer' button for this item. */}
                              {/* If the item has a designId, the design needs to be checked if it is owned by the user and only than the user should see the 'Open in Customizer' button for this item. */}
                              {item.uniqueCartId ||
                              assets?.[item.designId]?.isOwned ? (
                                <Button
                                  variant="light"
                                  className="open-design-button"
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        t('cart-screen-design-export-warning')
                                      )
                                    ) {
                                      navigate(
                                        `/customizer/${
                                          item.uniqueCartId
                                            ? item.uniqueCartId
                                            : item.designId
                                        }`
                                      );
                                    }
                                  }}
                                >
                                  <div className="btn-icon">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="currentColor"
                                      className="open-design-svg bi bi-box-arrow-left"
                                      viewBox="0 0 16 16"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M6 12.5a.5.5 0 0 0 .5.5h8a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5h-8a.5.5 0 0 0-.5.5v2a.5.5 0 0 1-1 0v-2A1.5 1.5 0 0 1 6.5 2h8A1.5 1.5 0 0 1 16 3.5v9a1.5 1.5 0 0 1-1.5 1.5h-8A1.5 1.5 0 0 1 5 12.5v-2a.5.5 0 0 1 1 0z"
                                      />
                                      <path
                                        fillRule="evenodd"
                                        d="M.146 8.354a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L1.707 7.5H10.5a.5.5 0 0 1 0 1H1.707l2.147 2.146a.5.5 0 0 1-.708.708z"
                                      />
                                    </svg>
                                    <span
                                      className="btn-icon-text"
                                      style={{ marginLeft: '6px' }}
                                    >
                                      {t('cart-screen-open-in-customizer')}
                                    </span>
                                  </div>
                                </Button>
                              ) : null}
                            </Col>

                            <Col
                              xs={8}
                              sm={6}
                              className="d-flex justify-content-center align-items-center"
                            >
                              <div className="cart-item-name">{item.name}</div>
                            </Col>

                            <Col
                              xs={2}
                              sm={3}
                              className="d-flex justify-content-end align-items-center"
                            >
                              <Button
                                variant="light"
                                onClick={() => removeItemHandler(item)}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="currentColor"
                                  className="remove-design-svg bi bi-trash"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                                  <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
                                </svg>
                              </Button>
                            </Col>
                          </Row>

                          <Row className="cart-img-container">
                            <img
                              src={
                                item.uniqueCartId
                                  ? assets[item.uniqueCartId]?.pngDataUrl
                                  : assets[item.designId]?.signedThumbnailUrl
                              }
                              alt={item.name}
                              className="img-fluid img-thumbnail"
                            ></img>
                          </Row>

                          <Row className="w-100">
                            <Col xs={6} className="cart-item-price">
                              <span>{formatEuro(item.unitGross)}</span>
                            </Col>
                            <Col xs={6} className="cart-item-quantity">
                              <Form.Control
                                className="mobile-device-compatible-flex-width"
                                type="number"
                                min="1"
                                max="100"
                                defaultValue={item.quantity}
                                onBlur={(e) => handleBlur(e, item)}
                                onWheel={handleWheel}
                              />
                            </Col>
                          </Row>
                        </ListGroup.Item>
                      </div>
                    ))}
                  </ListGroup>
                )}
              </Row>

              <Row className="cart-divider">
                <Card className="w-100">
                  {/* The Card component is used to create a flexible and extensible content container to display content in a structured and organized way, with a consistent look and feel across different parts of an application */}
                  <Card.Body>
                    {/* the Card.Body adds padding */}
                    <ListGroup variant="flush">
                      {/* variant="flush" removes the border around it */}
                      <ListGroup.Item className="cart-total">
                        <h3>
                          {t('cart-screen-subtotal-price')}{' '}
                          {formatEuro(itemsGross(cartItems))}
                        </h3>
                      </ListGroup.Item>
                      <ListGroup.Item className="cart-checkout">
                        <Button
                          type="button"
                          variant="primary"
                          className="w-100"
                          onClick={checkoutHandler}
                          disabled={cartItems.length === 0}
                        >
                          {t('cart-screen-proceed-to-checkout')}
                        </Button>
                      </ListGroup.Item>
                    </ListGroup>
                  </Card.Body>
                </Card>
              </Row>
            </Col>
          </Row>
        )}
      </div>
    </Container>
  );
}
